import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { Package, Plus, Pencil, Trash2, Loader2, X, Wrench, Box, PackagePlus } from 'lucide-react'
import { PageHero, Button, Input } from '@components/ui'
import { costService, Cost, CreateCostRequest } from '@services/costService'
import { getApiErrorMessage } from '@services/apiErrorHandler'
import { TECHNICAL_TEAM_ERROR_MESSAGE } from '@constants/userFacingErrors'
import { useAuthStore } from '@store/useAuthStore'

export function CatalogPage() {
  const { user } = useAuthStore()
  const queryClient = useQueryClient()
  const workshopCompanyId = user?.workshopCompanyId

  const [activeTab, setActiveTab] = useState<'services' | 'pieces'>('services')
  const [showModal, setShowModal] = useState(false)
  const [editingCost, setEditingCost] = useState<Cost | null>(null)
  const [form, setForm] = useState<CreateCostRequest>({
    workshopCompanyId: workshopCompanyId!,
    type: 'S',
    description: '',
    hourlyRate: undefined,
    unitPrice: undefined,
    quantity: 0,
    active: true,
  })
  const [showAddStockModal, setShowAddStockModal] = useState(false)
  const [addStockCost, setAddStockCost] = useState<Cost | null>(null)
  const [addStockQuantity, setAddStockQuantity] = useState(1)
  const [pageError, setPageError] = useState<string | null>(null)

  const costsQueryKey = ['costs', 'workshop', workshopCompanyId] as const

  const { data: costs = [], isLoading } = useQuery(
    costsQueryKey,
    () => costService.getByWorkshop(workshopCompanyId!),
    { enabled: !!workshopCompanyId }
  )

  const services = costs.filter((c) => c.type === 'S')
  const pieces = costs.filter((c) => c.type === 'P')
  const currentItems = activeTab === 'services' ? services : pieces

  const createMutation = useMutation(
    (data: CreateCostRequest) => costService.create(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(costsQueryKey)
        queryClient.invalidateQueries(['workshopCosts'])
        setShowModal(false)
        resetForm()
        setPageError(null)
      },
      onError: (err: unknown) => {
        setPageError(getApiErrorMessage(err, 'Erro ao criar item.'))
      },
    }
  )

  const updateMutation = useMutation(
    ({ id, data }: { id: number; data: CreateCostRequest }) => costService.update(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(costsQueryKey)
        queryClient.invalidateQueries(['workshopCosts'])
        setShowModal(false)
        setEditingCost(null)
        resetForm()
        setPageError(null)
      },
      onError: (err: unknown) => {
        setPageError(getApiErrorMessage(err, 'Erro ao atualizar item.'))
      },
    }
  )

  const deleteMutation = useMutation(
    (id: number) => costService.delete(id),
    {
      onMutate: async (id) => {
        setPageError(null)
        await queryClient.cancelQueries(costsQueryKey)
        const previousCosts = queryClient.getQueryData<Cost[]>(costsQueryKey)
        if (previousCosts) {
          queryClient.setQueryData<Cost[]>(
            costsQueryKey,
            previousCosts.filter((cost) => cost.id !== id)
          )
        }
        return { previousCosts }
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries(costsQueryKey)
        queryClient.invalidateQueries(['workshopCosts'])
      },
      onError: (err: unknown, _id, context) => {
        if (context?.previousCosts) {
          queryClient.setQueryData(costsQueryKey, context.previousCosts)
        }
        setPageError(getApiErrorMessage(err, TECHNICAL_TEAM_ERROR_MESSAGE))
      },
    }
  )

  const addStockMutation = useMutation(
    ({ id, quantity }: { id: number; quantity: number }) => costService.addStock(id, quantity),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(costsQueryKey)
        queryClient.invalidateQueries(['workshopCosts'])
        setShowAddStockModal(false)
        setAddStockCost(null)
        setAddStockQuantity(1)
        setPageError(null)
      },
      onError: (err: unknown) => {
        setPageError(getApiErrorMessage(err, 'Erro ao adicionar stock.'))
      },
    }
  )

  const resetForm = () => {
    setForm({
      workshopCompanyId: workshopCompanyId!,
      type: activeTab === 'services' ? 'S' : 'P',
      description: '',
      hourlyRate: undefined,
      unitPrice: undefined,
      quantity: activeTab === 'pieces' ? 0 : undefined,
      active: true,
    })
  }

  const openCreate = () => {
    setEditingCost(null)
    setForm({
      workshopCompanyId: workshopCompanyId!,
      type: activeTab === 'services' ? 'S' : 'P',
      description: '',
      hourlyRate: undefined,
      unitPrice: undefined,
      quantity: activeTab === 'pieces' ? 0 : undefined,
      active: true,
    })
    setShowModal(true)
  }

  const openEdit = (cost: Cost) => {
    setEditingCost(cost)
    setAddStockQuantity(1)
    setForm({
      workshopCompanyId: cost.workshopCompanyId,
      type: cost.type,
      description: cost.description,
      hourlyRate: cost.hourlyRate,
      unitPrice: cost.unitPrice,
      quantity: cost.type === 'P' ? (cost.quantity ?? 0) : undefined,
      active: cost.active,
    })
    setShowModal(true)
  }

  const handleSubmit = () => {
    if (!form.description?.trim()) {
      setPageError('A descrição é obrigatória.')
      return
    }
    if (form.type === 'S') {
      if (!form.hourlyRate || form.hourlyRate <= 0) {
        setPageError('A taxa horária é obrigatória e deve ser positiva.')
        return
      }
    } else {
      if (!form.unitPrice || form.unitPrice <= 0) {
        setPageError('O preço unitário é obrigatório e deve ser positivo.')
        return
      }
    }
    if (!workshopCompanyId) {
      setPageError('Utilizador sem oficina associada.')
      return
    }

    setPageError(null)

    const payload: CreateCostRequest = {
      ...form,
      workshopCompanyId,
      quantity: form.type === 'P' ? (form.quantity ?? 0) : undefined,
      createdBy: user?.id,
    }

    if (editingCost) {
      updateMutation.mutate({ id: editingCost.id, data: payload })
    } else {
      createMutation.mutate(payload)
    }
  }

  const handleDelete = (cost: Cost) => {
    if (!confirm(`Remover "${cost.description}" do catálogo?`)) return
    deleteMutation.mutate(cost.id)
  }

  if (!workshopCompanyId) {
    return (
      <div className="space-y-8">
        <PageHero title="CATÁLOGO" />
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-amber-800">
          <p>O seu utilizador não está associado a nenhuma oficina. Contacte o administrador.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <PageHero title="CATÁLOGO" />

      {pageError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {pageError}
        </div>
      )}

      <section className="rounded-[32px] border border-secondary-200 bg-white p-6 shadow-lg sm:p-8">
        <p className="mb-6 text-sm text-secondary-600">
          Configure os itens do catálogo da sua oficina. Serviços e peças podem ser adicionados às folhas de obra a partir deste catálogo.
        </p>

        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Button
              variant={activeTab === 'services' ? 'primary' : 'outline'}
              onClick={() => setActiveTab('services')}
              className="flex items-center gap-2"
            >
              <Wrench className="h-4 w-4" />
              Serviços ({services.length})
            </Button>
            <Button
              variant={activeTab === 'pieces' ? 'primary' : 'outline'}
              onClick={() => setActiveTab('pieces')}
              className="flex items-center gap-2"
            >
              <Box className="h-4 w-4" />
              Peças ({pieces.length})
            </Button>
          </div>
          <Button variant="primary" onClick={openCreate} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Adicionar {activeTab === 'services' ? 'Serviço' : 'Peça'}
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-secondary-500" />
          </div>
        ) : currentItems.length === 0 ? (
          <div className="py-12 text-center text-secondary-500">
            <Package className="mx-auto mb-4 h-12 w-12 text-secondary-400" />
            <p>Nenhum {activeTab === 'services' ? 'serviço' : 'peça'} no catálogo.</p>
            <p className="mt-1 text-sm">Adicione itens para poder usá-los nas folhas de obra.</p>
            <Button variant="primary" onClick={openCreate} className="mt-4">
              Adicionar primeiro item
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {currentItems.map((item) => (
              <div
                key={item.id}
                className="flex flex-col gap-3 rounded-lg border border-secondary-200 bg-white p-4 transition-colors hover:bg-secondary-50 md:flex-row md:items-center md:justify-between"
              >
                <div className="flex min-w-0 items-center gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100">
                    {item.type === 'S' ? (
                      <Wrench className="h-5 w-5 text-primary-600" />
                    ) : (
                      <Box className="h-5 w-5 text-primary-600" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="break-words font-semibold text-secondary-900">{item.description}</p>
                    <p className="text-sm text-secondary-500">
                      {item.type === 'S'
                        ? `${(item.hourlyRate ?? 0).toFixed(2)} €/h`
                        : `${(item.unitPrice ?? 0).toFixed(2)} €`}
                      {item.type === 'P' && (
                        <span className="ml-2 text-secondary-600">
                          • Stock: {item.quantity ?? 0}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row md:items-center">
                  {item.type === 'P' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setAddStockCost(item)
                        setAddStockQuantity(1)
                        setShowAddStockModal(true)
                      }}
                      className="hidden items-center gap-1 whitespace-nowrap md:flex"
                    >
                      <PackagePlus className="h-4 w-4" />
                      Adicionar stock
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEdit(item)}
                    className="flex w-full items-center justify-center gap-1 whitespace-nowrap md:w-auto"
                  >
                    <Pencil className="h-4 w-4" />
                    Editar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(item)}
                    disabled={deleteMutation.isLoading}
                    className="flex w-full items-center justify-center gap-1 whitespace-nowrap text-red-600 hover:bg-red-50 hover:border-red-200 md:w-auto"
                  >
                    <Trash2 className="h-4 w-4" />
                    Remover
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Modal Criar/Editar */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-2xl border border-secondary-200 bg-white p-6 shadow-xl">
            <div className="mb-6 flex items-center justify-between">
              <h3 className="text-xl font-bold text-secondary-900">
                {editingCost ? 'Editar' : 'Novo'} {form.type === 'S' ? 'Serviço' : 'Peça'}
              </h3>
              <button
                onClick={() => {
                  setShowModal(false)
                  setEditingCost(null)
                  resetForm()
                }}
                className="text-secondary-400 hover:text-secondary-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-secondary-700">Descrição *</label>
                <Input
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder={form.type === 'S' ? 'Ex: Substituição de pastilhas' : 'Ex: Pastilhas de freio'}
                />
              </div>

              {form.type === 'S' ? (
                <div>
                  <label className="mb-2 block text-sm font-medium text-secondary-700">Taxa horária (€/h) *</label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.hourlyRate ?? ''}
                    onChange={(e) => setForm({ ...form, hourlyRate: parseFloat(e.target.value) || undefined })}
                    placeholder="Ex: 45.00"
                  />
                </div>
              ) : (
                <>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-secondary-700">Preço unitário (€) *</label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.unitPrice ?? ''}
                      onChange={(e) => setForm({ ...form, unitPrice: parseFloat(e.target.value) || undefined })}
                      placeholder="Ex: 60.00"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-secondary-700">Stock inicial</label>
                    <Input
                      type="number"
                      min="0"
                      value={form.quantity ?? 0}
                      onChange={(e) => setForm({ ...form, quantity: parseInt(e.target.value, 10) || 0 })}
                      placeholder="0"
                    />
                  </div>
                  {editingCost && (
                    <div className="rounded-xl border border-secondary-200 bg-secondary-50 p-3 md:hidden">
                      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-secondary-800">
                        <PackagePlus className="h-4 w-4 text-primary-600" />
                        Adicionar stock
                      </div>
                      <div className="flex flex-col gap-2 sm:flex-row">
                        <Input
                          type="number"
                          min="1"
                          value={addStockQuantity}
                          onChange={(e) => setAddStockQuantity(parseInt(e.target.value, 10) || 1)}
                        />
                        <Button
                          variant="outline"
                          onClick={() => addStockMutation.mutate({ id: editingCost.id, quantity: addStockQuantity })}
                          isLoading={addStockMutation.isLoading}
                          className="w-full whitespace-nowrap sm:w-auto"
                        >
                          Adicionar
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className="flex items-center gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowModal(false)
                    setEditingCost(null)
                    resetForm()
                  }}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  isLoading={createMutation.isLoading || updateMutation.isLoading}
                  className="flex-1"
                >
                  {editingCost ? 'Guardar' : 'Criar'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Adicionar Stock */}
      {showAddStockModal && addStockCost && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-sm rounded-2xl border border-secondary-200 bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-secondary-900">Adicionar stock</h3>
              <button
                onClick={() => {
                  setShowAddStockModal(false)
                  setAddStockCost(null)
                }}
                className="text-secondary-400 hover:text-secondary-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="mb-4 text-sm text-secondary-600">{addStockCost.description}</p>
            <p className="mb-4 text-sm text-secondary-500">
              Stock atual: <strong>{addStockCost.quantity ?? 0}</strong>
            </p>
            <div className="mb-6">
              <label className="mb-2 block text-sm font-medium text-secondary-700">Quantidade a adicionar</label>
              <Input
                type="number"
                min="1"
                value={addStockQuantity}
                onChange={(e) => setAddStockQuantity(parseInt(e.target.value, 10) || 1)}
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddStockModal(false)
                  setAddStockCost(null)
                }}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={() => addStockMutation.mutate({ id: addStockCost.id, quantity: addStockQuantity })}
                isLoading={addStockMutation.isLoading}
                className="flex-1"
              >
                Adicionar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
